import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, User, Phone, Loader2 } from "lucide-react";
import { registerCustomer } from "@/Calls/callCustomerAuth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function CustomerSignUpPage() {
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const navigate = useNavigate();

    const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (form.password !== form.confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (form.password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }

        setLoading(true);
        try {
            await registerCustomer({
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                phone: form.phone || undefined,
                password: form.password,
            });
            navigate("/customer/login", { replace: true, state: { registered: true } });
        } catch (err) {
            if (err instanceof Error) setError(err.message);
            else setError("Registration failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden p-4">
            <div className="z-10 w-full max-w-6xl">
                <div className="bg-secondary/50 overflow-hidden rounded-[40px] shadow-2xl">
                    <div className="grid min-h-[700px] lg:grid-cols-2">
                        {/* LEFT — image */}
                        <div
                            className="relative m-4 rounded-3xl bg-cover bg-center p-12 text-white hidden lg:block"
                            style={{ backgroundImage: "url('/images/assets/dealership_customer_futuristic_1.png')" }}
                        >
                            <div className="absolute inset-0 rounded-3xl bg-black/30" />
                            <div className="relative z-10 mt-auto pt-64">
                                <h3 className="text-2xl font-light uppercase">Your Car. Your Terms.</h3>
                                <p className="mt-2 text-sm text-white/80">
                                    List your vehicle for trade-in or private sale — right from your phone.
                                </p>
                            </div>
                        </div>

                        {/* RIGHT — form */}
                        <div className="flex flex-col justify-center p-12">
                            <div className="mx-auto w-full max-w-md">
                                <div className="mb-8 text-center">
                                    <h2 className="text-3xl font-light uppercase">Create Your Account</h2>
                                    <p className="mt-2 text-sm text-stone-600">
                                        Free — list trade-ins, FSBO vehicles, and more
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Name row */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="mb-1 block text-xs font-medium uppercase">First Name</label>
                                            <div className="relative">
                                                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                <Input
                                                    value={form.firstName}
                                                    onChange={set("firstName")}
                                                    required
                                                    placeholder="First"
                                                    className="pl-9"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-xs font-medium uppercase">Last Name</label>
                                            <Input
                                                value={form.lastName}
                                                onChange={set("lastName")}
                                                required
                                                placeholder="Last"
                                            />
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="mb-1 block text-xs font-medium uppercase">Email</label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <Input
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                required
                                                placeholder="your@email.com"
                                                className="pl-9"
                                            />
                                        </div>
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <label className="mb-1 block text-xs font-medium uppercase">Phone (optional)</label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <Input
                                                type="tel"
                                                value={form.phone}
                                                onChange={set("phone")}
                                                placeholder="(555) 000-0000"
                                                className="pl-9"
                                            />
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div>
                                        <label className="mb-1 block text-xs font-medium uppercase">Password</label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <Input
                                                type={showPassword ? "text" : "password"}
                                                value={form.password}
                                                onChange={set("password")}
                                                required
                                                placeholder="Min 8 characters"
                                                className="pl-9 pr-12"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confirm Password */}
                                    <div>
                                        <label className="mb-1 block text-xs font-medium uppercase">Confirm Password</label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <Input
                                                type="password"
                                                value={form.confirmPassword}
                                                onChange={set("confirmPassword")}
                                                required
                                                placeholder="Repeat your password"
                                                className="pl-9"
                                            />
                                        </div>
                                    </div>

                                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="relative flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-3 text-sm font-medium text-white transition-all duration-300 hover:opacity-90"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                <span className="ml-2">Creating account…</span>
                                            </>
                                        ) : (
                                            "Create Free Account"
                                        )}
                                    </Button>

                                    <p className="text-center text-sm text-stone-500">
                                        Already have an account?{" "}
                                        <Link to="/customer/login" className="text-indigo-500 hover:underline font-medium">
                                            Sign in
                                        </Link>
                                    </p>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
